import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
import { Tooltip } from '@mui/material';
import { message } from 'antd';
import './VoiceInput.css';

/**
 * VoiceInput component for speech recognition
 * Uses the Web Speech API (webkitSpeechRecognition)
 */
const VoiceInput = memo(({
    onTranscript,
    onListeningChange,
    disabled = false,
    language = 'en-US'
}) => {
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const recognitionRef = useRef(null);
    const finalTranscriptRef = useRef('');

    // Check for browser support
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            setIsSupported(true);
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = language;

            recognitionRef.current.onresult = (event) => {
                let interimTranscript = '';
                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript;
                    } else {
                        interimTranscript += transcript;
                    }
                }

                // Accumulate final transcripts
                if (finalTranscript) {
                    finalTranscriptRef.current += (finalTranscriptRef.current ? ' ' : '') + finalTranscript;
                }

                // Pass the combined transcript
                const fullTranscript = finalTranscriptRef.current + (interimTranscript ? ' ' + interimTranscript : '');
                if (onTranscript) {
                    onTranscript(fullTranscript.trim(), !!finalTranscript);
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                if (event.error === 'not-allowed') {
                    message.error('Microphone access denied. Please allow microphone access in your browser settings.');
                } else if (event.error === 'no-speech') {
                    // This is common, don't show error
                } else if (event.error !== 'aborted') {
                    message.error(`Speech recognition error: ${event.error}`);
                }
                setIsListening(false);
                if (onListeningChange) onListeningChange(false);
            };

            recognitionRef.current.onend = () => {
                // If still listening, restart (handles browser timeout)
                if (isListening && recognitionRef.current) {
                    try {
                        recognitionRef.current.start();
                    } catch (e) {
                        // Recognition already started
                    }
                } else {
                    setIsListening(false);
                    if (onListeningChange) onListeningChange(false);
                }
            };
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, [language, isListening, onTranscript, onListeningChange]);

    const toggleListening = useCallback(() => {
        if (!isSupported) {
            message.warning('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
            return;
        }

        if (isListening) {
            // Stop listening
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            setIsListening(false);
            if (onListeningChange) onListeningChange(false);
        } else {
            // Start listening
            finalTranscriptRef.current = '';
            try {
                recognitionRef.current.start();
                setIsListening(true);
                if (onListeningChange) onListeningChange(true);
            } catch (e) {
                console.error('Failed to start speech recognition:', e);
                message.error('Failed to start speech recognition');
            }
        }
    }, [isSupported, isListening, onListeningChange]);

    if (!isSupported) {
        return null; // Don't render if not supported
    }

    return (
        <Tooltip
            title={isListening ? "Stop listening" : "Voice input"}
            arrow
            placement="top"
        >
            <span style={{ display: 'inline-block' }}>
                <button
                    className={`ntb-voice-input-btn ${isListening ? 'listening' : ''}`}
                    onClick={toggleListening}
                    disabled={disabled}
                    aria-label={isListening ? "Stop voice input" : "Start voice input"}
                    type="button"
                >
                    {isListening ? (
                        <>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                <rect x="6" y="6" width="12" height="12" rx="2" />
                            </svg>
                            <span className="ntb-voice-pulse-ring" />
                            <span className="ntb-voice-pulse-ring delay" />
                        </>
                    ) : (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                        </svg>
                    )}
                </button>
            </span>
        </Tooltip>
    );
});

VoiceInput.displayName = 'VoiceInput';

export default VoiceInput;
